import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useAcceptTeamInvite } from "@/hooks/useShelterTeam";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Users, XCircle } from "lucide-react";
import { AuthHeroIcon } from "@/components/auth/AuthHeroIcon";

const AcceptTeamInvite = () => {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = params.get("token") ?? "";

  const accept = useAcceptTeamInvite();
  const [state, setState] = useState<"idle" | "running" | "ok" | "err">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [shelterName, setShelterName] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const next = `/panel/zespol/dolacz?token=${encodeURIComponent(token)}`;
      navigate(`/logowanie?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    if (!token) {
      setState("err");
      setErrorMsg("Brak tokena zaproszenia w linku");
      return;
    }
    if (state !== "idle") return;
    setState("running");
    accept
      .mutateAsync(token)
      .then((res) => {
        setShelterName(res.shelter?.name ?? null);
        setState("ok");
        qc.invalidateQueries({ queryKey: ["owned-shelters", user.id] });
        qc.invalidateQueries({ queryKey: ["shelter-team"] });
      })
      .catch((e: any) => {
        setErrorMsg(e?.message ?? "Nie udało się przyjąć zaproszenia");
        setState("err");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, token]);

  return (
    <>
      <Helmet>
        <title>Zaproszenie do zespołu | Panel</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-xl mx-auto">
        <Card>
          <CardHeader className="p-4 sm:p-6 pb-2">
            <CardTitle className="text-base sm:text-lg">Zaproszenie do zespołu schroniska</CardTitle>
            <CardDescription>
              Po przyjęciu zaproszenia otrzymasz dostęp do edycji danych schroniska.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-2 space-y-4">
            {state === "running" && (
              <div className="flex flex-col items-center text-center gap-4 py-6">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 blur-xl" />
                  <div className="relative h-16 w-16 rounded-full bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                    <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Przyjmujemy zaproszenie...</p>
              </div>
            )}
            {state === "ok" && (
              <div className="space-y-5 py-2">
                <div className="flex flex-col items-center text-center gap-4 pt-2">
                  <AuthHeroIcon icon={Users} tone="primary" sparkle />
                  <div className="space-y-1">
                    <p className="text-lg font-bold">Witamy w zespole!</p>
                    {shelterName && (
                      <p className="text-sm text-muted-foreground max-w-sm">
                        Dołączyłaś/eś do zespołu schroniska <strong className="text-foreground">{shelterName}</strong>.
                      </p>
                    )}
                  </div>
                </div>


                <div className="flex justify-center">
                  <Button asChild size="lg" className="gap-2">
                    <Link to="/panel">Przejdź do panelu →</Link>
                  </Button>
                </div>
              </div>
            )}
            {state === "err" && (
              <div className="flex flex-col items-center text-center gap-5 py-4">
                <AuthHeroIcon icon={XCircle} tone="destructive" />
                <div className="space-y-1">
                  <p className="text-base font-semibold">Nie udało się przyjąć zaproszenia</p>
                  <p className="text-sm text-muted-foreground max-w-sm">{errorMsg}</p>
                </div>
                <Button asChild variant="outline" size="lg">
                  <Link to="/panel">Wróć do panelu</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AcceptTeamInvite;
